import { useAtomValue } from 'jotai';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiErrorMessage } from '@/lib/utils';
import { ACTION_LOG_PAGE_SIZE, socialAgentService } from '@/services/social-agent.service';
import { resolveActionLogWindow, type ActionLogFilters, type ActionLogWindow } from '../action-log-constants';
import { toActionLogSearchString } from '../action-log-query';
import type { SocialActionLogRow } from '../types';

/**
 * Reading the action log, one keyset page at a time.
 *
 * Not react-query: every other social-agent query is parameterless and lives in a Jotai atom, and
 * a cursor-paginated list keyed on five filter axes does not fit that shape. What it needs instead
 * is small and explicit — accumulate pages, throw them away when the filters change, and never let
 * a response from a superseded filter set land on the current one.
 */

export interface UseActionLogsResult {
	rows: SocialActionLogRow[];
	/** First page in flight. The table shows a skeleton, not an empty state. */
	isLoading: boolean;
	/** A follow-up page in flight. The rows already on screen stay put. */
	isLoadingMore: boolean;
	error: string | null;
	hasMore: boolean;
	loadMore: () => void;
	retry: () => void;
	/**
	 * The window every request in this session used.
	 *
	 * Resolved once per filter change and handed back so the export and the footer describe the
	 * SAME instants the rows were fetched with, rather than re-resolving "last 7 days" a minute later.
	 */
	appliedWindow: ActionLogWindow;
}

export const useActionLogs = (filters: ActionLogFilters): UseActionLogsResult => {
	const appliedWindow = useMemo(() => resolveActionLogWindow(filters.range), [filters.range]);

	// The serialised query, not the objects, decides when to refetch. Filter objects are rebuilt on
	// every render of the page above; comparing what actually goes on the wire is what makes the
	// effect fire once per real change instead of once per keystroke elsewhere on the page.
	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const buScope = useMemo(
		() => (selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {}),
		[selectedBusinessUnitId]
	);

	// The brand is part of the query, so it belongs in the key that decides when to refetch.
	// Without it, switching brand leaves the previous brand's moderation history on screen.
	const queryKey = useMemo(
		() => `${toActionLogSearchString({ window: appliedWindow, filters })}&bu=${selectedBusinessUnitId ?? ''}`,
		[appliedWindow, filters, selectedBusinessUnitId]
	);

	const [rows, setRows] = useState<SocialActionLogRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [reloadToken, setReloadToken] = useState(0);

	/**
	 * Which filter set the newest request belongs to.
	 *
	 * A slow first page for one filter set can land after a fast first page for the next. Without
	 * this, the operator sees rows that do not match the bar above them — which on an audit screen
	 * is not a glitch, it is a wrong answer to "what did the agent do?".
	 */
	const activeQueryKey = useRef(queryKey);

	// Kept in a ref so `loadMore` is stable and does not re-create the footer button's handler on
	// every page that arrives.
	const paginationRef = useRef({ window: appliedWindow, filters, nextCursor });
	paginationRef.current = { window: appliedWindow, filters, nextCursor };

	useEffect(() => {
		activeQueryKey.current = queryKey;
		let cancelled = false;

		setIsLoading(true);
		setError(null);
		setNextCursor(null);

		socialAgentService
			.listActionLogs({ window: appliedWindow, filters, limit: ACTION_LOG_PAGE_SIZE }, buScope)
			.then((page) => {
				if (cancelled || activeQueryKey.current !== queryKey) return;
				setRows(page.logs ?? []);
				setNextCursor(page.pagination?.nextCursor ?? null);
			})
			.catch((err: unknown) => {
				if (cancelled || activeQueryKey.current !== queryKey) return;
				console.error('[useActionLogs] Failed to load the first page of action logs', { queryKey, error: err });
				// The rows on screen belonged to a filter set that is no longer selected, so leaving
				// them up would caption stale rows with the new filters.
				setRows([]);
				setError(getApiErrorMessage(err, 'Could not load the action log.'));
			})
			.finally(() => {
				if (!cancelled && activeQueryKey.current === queryKey) setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [buScope, queryKey, reloadToken, appliedWindow, filters]);

	const loadMore = useCallback(() => {
		const { window: pageWindow, filters: pageFilters, nextCursor: cursor } = paginationRef.current;
		if (!cursor) return;

		const requestedAt = activeQueryKey.current;
		setIsLoadingMore(true);
		setError(null);

		socialAgentService
			.listActionLogs({ window: pageWindow, filters: pageFilters, limit: ACTION_LOG_PAGE_SIZE, cursor }, buScope)
			.then((page) => {
				if (activeQueryKey.current !== requestedAt) return;
				// Appended, never merged or de-duplicated: the log writes one row per rule-decision, so
				// two rows for the same comment are two different facts, not a repeat.
				setRows((current) => [...current, ...(page.logs ?? [])]);
				setNextCursor(page.pagination?.nextCursor ?? null);
			})
			.catch((err: unknown) => {
				if (activeQueryKey.current !== requestedAt) return;
				console.error('[useActionLogs] Failed to load a further page of action logs', {
					queryKey: requestedAt,
					cursor,
					error: err
				});
				// The loaded rows are still correct — only the extension failed, so they stay.
				setError(getApiErrorMessage(err, 'Could not load more rows.'));
			})
			.finally(() => {
				if (activeQueryKey.current === requestedAt) setIsLoadingMore(false);
			});
		// `buScope` is a real dependency: without it this callback keeps the brand it closed over,
		// so a "load more" after a brand switch would page through the PREVIOUS brand's rows and
		// append them to the new brand's list.
	}, [buScope]);

	const retry = useCallback(() => setReloadToken((token) => token + 1), []);

	return {
		rows,
		isLoading,
		isLoadingMore,
		error,
		hasMore: nextCursor !== null,
		loadMore,
		retry,
		appliedWindow
	};
};
